import type { ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import { usePortalAuth } from '../auth/PortalAuth'

const TABS = [
  { to: '/', label: 'Overview', end: true },
  { to: '/shipments', label: 'Shipments' },
  { to: '/orders', label: 'Orders' },
  { to: '/invoices', label: 'Invoices' },
  { to: '/claims', label: 'Claims' },
]

/**
 * The demo warning, in the portal's terms.
 *
 * A customer raising a claim here has to know it is going nowhere — the portal
 * is where somebody is most likely to type something they expect a human to
 * read.
 */
function DemoNotice() {
  const { user } = usePortalAuth()

  const config = useQuery({
    queryKey: ['public-config'],
    queryFn: () => api.anonymous<{ demo_tenant: string | null }>('/config').then((r) => r.data),
    retry: false,
    staleTime: Infinity,
  })

  const isDemo = Boolean(config.data?.demo_tenant) && config.data?.demo_tenant === user?.tenant.slug

  if (!isDemo) return null

  return (
    <div className="banner banner-warn">
      <strong>This is a demo account.</strong> Anything you enter here — including a claim — is
      wiped and rebuilt on a schedule, and nobody is reading it.
    </div>
  )
}

function initials(name: string): string {
  return name
    .split(/[\s(]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

export function Layout({ children }: { children: ReactNode }) {
  const { user, logout } = usePortalAuth()

  return (
    <div className="shell">
      <header className="header">
        <div className="brand">
          <div className="brand-mark">📦</div>
          <div>
            <div className="brand-name">Fleey</div>
            {/* Whose portal this is, not whose software it is — the customer
                deals with the transporter, not with us. */}
            <div className="brand-sub">{user?.tenant.name ?? 'Customer portal'}</div>
          </div>
        </div>

        <nav className="tabs">
          {TABS.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) => `tab ${isActive ? 'active' : ''}`}
            >
              {tab.label}
            </NavLink>
          ))}
        </nav>

        <div className="who">
          <div style={{ textAlign: 'right' }}>
            <div className="who-name">{user?.customer.name}</div>
            <div className="who-sub">{user?.name}</div>
          </div>
          <div className="avatar">{initials(user?.customer.name ?? '?')}</div>
          <button className="btn btn-sm" onClick={logout}>
            Sign out
          </button>
        </div>
      </header>

      <main className="content">
        <div className="page">
          <DemoNotice />
          {children}
        </div>
      </main>
    </div>
  )
}
